// src/app/services/push/push.service.ts
import { Injectable, NgZone, OnDestroy, inject } from "@angular/core";
import { Router } from "@angular/router";
import { Capacitor } from "@capacitor/core";
import { Device } from "@capacitor/device";
import { BehaviorSubject, Subscription } from "rxjs";

import { FirebaseApp } from "@angular/fire/app";
import {
  getMessaging,
  getToken as getWebToken,
  deleteToken as deleteWebToken,
  isSupported,
  onMessage,
  MessagePayload,
} from "firebase/messaging";

import {
  FirebaseMessaging,
  Importance,
  Visibility,
  PermissionStatus,
} from "@capacitor-firebase/messaging";

import { serverTimestamp } from "@angular/fire/firestore";

import { ApiService } from "../api/api.service";
import { AuthService } from "../auth/auth.service";
import { GlobalService } from "../global/global.service";
import { environment } from "../../../environments/environment";
import { Strings } from "../../enum/strings.enum";

export interface ForegroundPushEvent {
  title: string;
  body: string;
  data?: any;
}

@Injectable({ providedIn: "root" })
export class PushService implements OnDestroy {
  private api = inject(ApiService);
  private auth = inject(AuthService);
  private global = inject(GlobalService);
  private router = inject(Router);
  private ngZone = inject(NgZone);
  private fbApp = inject(FirebaseApp);

  private authSub?: Subscription;
  private inited = false;

  private deviceId: string | null = null;
  private currentUid: string | null = null;

  private readonly PROMPT_KEY = "push_prompted_v1";
  private readonly WEB_DEVICE_KEY = "web_device_id_v1";
  private readonly PENDING_TOKEN_KEY = "pending_fcm_token_v1";

  private readonly appKey = (environment as any).appKey || "customer";
  private readonly role = Strings.TYPE;

  private tokenRetryRunning = false;

  private _foreground = new BehaviorSubject<ForegroundPushEvent | null>(null);
  foreground$ = this._foreground.asObservable();

  // ------------------------
  // Public API
  // ------------------------
  async init(): Promise<void> {
    if (this.inited) return;
    this.inited = true;

    try {
      await this.ensureDeviceId();
      await this.ensureAndroidChannels();

      if (Capacitor.isNativePlatform()) {
        await this.initNativeListeners();
        void this.ensureNativeTokenWithRetry("init");
      } else {
        await this.initWebForegroundListener();
        this.initWebNotificationClickListener();
      }

      this.authSub = this.auth.userId.subscribe((u: any) => {
        const uid = u?.uid || null;
        void this.handleAuthChange(uid);
      });

      const uidNow = await this.auth.getId();
      if (uidNow) await this.handleAuthChange(uidNow);
      else await this.tryFlushPendingToken();

      console.log("[Push] init done (platform=" + Capacitor.getPlatform() + ")");
    } catch (e: any) {
      console.error("[Push] init crashed:", e?.message || e);
    }
  }

  ngOnDestroy(): void {
    try { this.authSub?.unsubscribe(); } catch {}
  }

  async requestPermissionAndRegister(): Promise<boolean> {
    const uid = await this.auth.getId();
    if (!uid) return false;

    await this.ensureDeviceId();

    // ---------- Native ----------
    if (Capacitor.isNativePlatform()) {
      try {
        const permBefore = await FirebaseMessaging.checkPermissions();
        console.log("[Push] native checkPermissions receive=" + (permBefore?.receive || "unknown"));

        const permAfter = await FirebaseMessaging.requestPermissions();
        console.log("[Push] native requestPermissions receive=" + (permAfter?.receive || "unknown"));

        if (this.isIos() && permAfter?.receive !== "granted") return false;

        // ✅ always safe token getter
        const token = await this.getNativeFcmTokenSafe("after-permission");
        if (token) await this.onToken(token);

        if (!token) void this.ensureNativeTokenWithRetry("after-permission");

        return !!token;
      } catch (e: any) {
        console.error("[Push] native permission/token flow failed:", e?.message || e);
        return false;
      }
    }

    // ---------- Web ----------
    if (!(await this.isWebMessagingSupported())) return false;
    if (!("Notification" in window)) return false;

    const permission = await Notification.requestPermission();
    if (permission !== "granted") return false;

    const token = await this.getWebFcmToken();
    console.log("[Push] web getToken len=" + (token?.length || 0));
    if (token) await this.onToken(token);

    return !!token;
  }

  async disableForThisDevice(uidOverride?: string | null): Promise<void> {
    const uid = uidOverride ?? this.currentUid;

    try {
      if (Capacitor.isNativePlatform()) {
        await FirebaseMessaging.deleteToken();
      } else if (await this.isWebMessagingSupported()) {
        const messaging = getMessaging(this.fbApp);
        await deleteWebToken(messaging);
      }
    } catch {}

    if (!uid || !this.deviceId) return;

    try {
      await this.api.updateDocument(`users/${uid}/devices/${this.deviceId}`, {
        enabled: false,
        updated_at: serverTimestamp(),
      });
      console.log("[Push] device disabled uid=" + uid + " deviceId=" + this.deviceId);
    } catch (e: any) {
      console.warn("[Push] disable device doc failed:", e?.message || e);
    }
  }

  // ------------------------
  // Internals
  // ------------------------
  private async handleAuthChange(uid: string | null): Promise<void> {
    const prevUid = this.currentUid;
    if (uid === prevUid) return;

    this.currentUid = uid;

    if (!uid) {
      await this.disableForThisDevice(prevUid);
      return;
    }

    await this.registerIfPermittedOrPromptOnce();
    await this.tryFlushPendingToken();

    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android") {
      void this.ensureNativeTokenWithRetry("auth-change");
    }
  }

  private async getNativeFcmTokenSafe(reason: string, timeoutMs = 8000): Promise<string | null> {
    try {
      const res = await Promise.race([
        FirebaseMessaging.getToken(),
        new Promise<null>((resolve) => setTimeout(() => resolve(null), timeoutMs)),
      ]);

      const token = (res as any)?.token || null;
      console.log(`[Push] getTokenSafe(${reason}) tokenLen=${token?.length || 0}`);
      return token;
    } catch (e: any) {
      console.warn(`[Push] getTokenSafe(${reason}) failed:`, e?.message || e);
      return null;
    }
  }

  private async initNativeListeners(): Promise<void> {
    try {
      await FirebaseMessaging.addListener("tokenReceived", (e) => {
        const token = e?.token || "";
        console.log("[Push] tokenReceived len=" + token.length);
        void this.onToken(token);
      });

      await FirebaseMessaging.addListener("notificationReceived", (e) => {
        const n = e?.notification as any;
        void this.handleForegroundNotification(n?.title, n?.body, n?.data);
      });

      await FirebaseMessaging.addListener("notificationActionPerformed", (e) => {
        const n = e?.notification as any;
        void this.handleNotificationClick(n?.data || n);
      });

      const token = await this.getNativeFcmTokenSafe("init");
      if (token) await this.onToken(token);
    } catch (e: any) {
      console.warn("[Push] initNativeListeners failed:", e?.message || e);
    }
  }

  private async ensureNativeTokenWithRetry(reason: string): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    if (this.tokenRetryRunning) return;

    this.tokenRetryRunning = true;
    try {
      const maxAttempts = 6;
      const delayMs = 1500;

      for (let i = 1; i <= maxAttempts; i++) {
        const token = await this.getNativeFcmTokenSafe(`${reason}-attempt${i}`, 6000);

        console.log(`[Push] retry(${reason}) attempt=${i}/${maxAttempts} tokenLen=${token?.length || 0}`);

        if (token) {
          await this.onToken(token);
          return;
        }

        await new Promise((res) => setTimeout(res, delayMs));
      }

      console.warn("[Push] token still empty after retries (" + reason + ")");
    } finally {
      this.tokenRetryRunning = false;
    }
  }

  private initWebNotificationClickListener(): void {
    try {
      if (!navigator?.serviceWorker) return;

      navigator.serviceWorker.addEventListener("message", (evt: any) => {
        if (evt?.data?.type === "notificationclick") {
          void this.handleNotificationClick(evt.data.data);
        }
      });
    } catch {}
  }

  private async registerIfPermittedOrPromptOnce(): Promise<void> {
    const uid = await this.auth.getId();
    if (!uid) return;

    const alreadyPrompted = localStorage.getItem(this.PROMPT_KEY) === "true";

    if (Capacitor.isNativePlatform()) {
      const platform = Capacitor.getPlatform();

      if (platform === "android") {
        void this.ensureNativeTokenWithRetry("android-register");

        try {
          const perm = await FirebaseMessaging.checkPermissions();
          const receive = perm?.receive || "unknown";

          if (receive !== "granted" && !alreadyPrompted) {
            localStorage.setItem(this.PROMPT_KEY, "true");
            this.softPrompt();
          }
        } catch {}

        return;
      }

      // iOS
      let perm: PermissionStatus | null = null;
      try { perm = await FirebaseMessaging.checkPermissions(); } catch {}

      if (perm?.receive === "granted") {
        const token = await this.getNativeFcmTokenSafe("ios-register");
        if (token) await this.onToken(token);
        return;
      }

      if (!alreadyPrompted) {
        localStorage.setItem(this.PROMPT_KEY, "true");
        this.softPrompt();
      }
      return;
    }

    // Web
    if (!(await this.isWebMessagingSupported())) return;
    if (!("Notification" in window)) return;

    if (Notification.permission === "granted") {
      const token = await this.getWebFcmToken();
      console.log("[Push] web token len=" + (token?.length || 0));
      if (token) await this.onToken(token);
      return;
    }

    if (!alreadyPrompted) {
      localStorage.setItem(this.PROMPT_KEY, "true");
      this.softPrompt();
    }
  }

  private softPrompt(): void {
    this.global.showAlert(
      "للاستفادة من تحديثات الطلب والعروض، يرجى تفعيل الإشعارات.",
      "تفعيل الإشعارات",
      [
        { text: "لاحقاً", role: "cancel" },
        {
          text: "تفعيل",
          handler: () => {
            void this.requestPermissionAndRegister().then((ok) => {
              if (!ok) this.global.showToast("لم يتم منح إذن الإشعارات", "warning", "bottom");
            });
          },
        },
      ],
      null,
      true
    );
  }

  private async onToken(token?: string): Promise<void> {
    await this.ensureDeviceId();
    const uid = await this.auth.getId();

    console.log(`[Push] onToken uid=${uid || "null"} deviceId=${this.deviceId || "null"} tokenLen=${token?.length || 0}`);

    if (!token) return;

    if (!uid) {
      try { localStorage.setItem(this.PENDING_TOKEN_KEY, token); } catch {}
      return;
    }

    // ✅ ensure JS Auth session exists (important on native after phone auth)
    try {
      await this.auth.waitForAuthReady(40, 250);
    } catch {
      try { localStorage.setItem(this.PENDING_TOKEN_KEY, token); } catch {}
      console.warn("[Push] auth not ready; stored pending token");
      return;
    }

    if (!this.deviceId) return;

    const platform = Capacitor.getPlatform();
    const deviceInfo = await this.safeDeviceInfo();

    const data = {
      token,
      enabled: true,
      platform,
      app: this.appKey,
      role: this.role,
      deviceInfo: deviceInfo ?? null,
      last_seen_at: serverTimestamp(),
      updated_at: serverTimestamp(),
    };

    const path = `users/${uid}/devices/${this.deviceId}`;

    try {
      // ✅ merge write (won’t overwrite other fields)
      await this.api.setDocument(path, data, true);
      console.log("[Push] ✅ device saved path=" + path + " tokenLen=" + token.length);

      try { localStorage.removeItem(this.PENDING_TOKEN_KEY); } catch {}
    } catch (e: any) {
      const code = e?.code || e?.name || "unknown";
      const msg = e?.message || JSON.stringify(e) || String(e);
      console.error("[Push] ❌ FAILED saving device doc path=" + path + " code=" + code + " msg=" + msg);

      try { localStorage.setItem(this.PENDING_TOKEN_KEY, token); } catch {}
    }
  }

  private async tryFlushPendingToken(): Promise<void> {
    const uid = await this.auth.getId();
    if (!uid) return;

    let token: string | null = null;
    try { token = localStorage.getItem(this.PENDING_TOKEN_KEY); } catch { token = null; }

    if (!token) return;
    console.log("[Push] flushing pending token len=" + token.length);
    await this.onToken(token);
  }

  private async handleForegroundNotification(title?: string, body?: string, data?: any): Promise<void> {
    const t = (title || "Mega Delivery").trim();
    const b = (body || "").trim();

    if (b) this.global.showToast(b, "dark", "top", 3500);
    try { this.global.playAudio(); } catch {}

    this._foreground.next({ title: t, body: b, data });
  }

  private async handleNotificationClick(data: any): Promise<void> {
    const route = data?.route || data?.url || data?.click_action;

    if (typeof route === "string" && route.trim()) {
      const r = route.trim();

      const safeRoute = r.startsWith("http")
        ? (() => {
            try {
              const u = new URL(r);
              return u.pathname + u.search + u.hash;
            } catch {
              return r;
            }
          })()
        : r;

      this.ngZone.run(() => this.router.navigateByUrl(safeRoute));
      return;
    }

    const orderId = data?.orderId || data?.order_id;
    if (orderId) {
      this.ngZone.run(() => this.router.navigateByUrl(`/app/tabs/orders/${orderId}`));
      return;
    }

    this.ngZone.run(() => this.router.navigateByUrl("/app/home"));
  }

  private async ensureDeviceId(): Promise<void> {
    if (this.deviceId) return;

    if (Capacitor.isNativePlatform()) {
      const id = await Device.getId();
      this.deviceId = id?.identifier || `native_${Math.random().toString(16).slice(2)}`;
      return;
    }

    const existing = localStorage.getItem(this.WEB_DEVICE_KEY);
    if (existing) {
      this.deviceId = existing;
      return;
    }

    const created = `web_${crypto?.randomUUID?.() || Math.random().toString(16).slice(2)}`;
    localStorage.setItem(this.WEB_DEVICE_KEY, created);
    this.deviceId = created;
  }

  private async safeDeviceInfo(): Promise<any> {
    try {
      if (Capacitor.isNativePlatform()) {
        const info = await Device.getInfo();
        return {
          model: info.model,
          platform: info.platform,
          osVersion: info.osVersion,
          manufacturer: info.manufacturer,
        };
      }
      return { ua: navigator.userAgent };
    } catch {
      return null;
    }
  }

  private async initWebForegroundListener(): Promise<void> {
    if (!(await this.isWebMessagingSupported())) return;

    const messaging = getMessaging(this.fbApp);
    onMessage(messaging, (payload: MessagePayload) => {
      const title = payload.notification?.title || (payload.data as any)?.title;
      const body = payload.notification?.body || (payload.data as any)?.body;
      void this.handleForegroundNotification(title, body, payload.data);
    });
  }

  private async getWebFcmToken(): Promise<string | null> {
    try {
      const vapidKey = environment.webPushVapidKey?.trim?.() || "";
      if (!vapidKey) throw new Error("Missing webPushVapidKey in environment");
      if (!navigator?.serviceWorker) throw new Error("No service worker");

      const reg = await navigator.serviceWorker.ready;
      const messaging = getMessaging(this.fbApp);

      return await getWebToken(messaging, { vapidKey, serviceWorkerRegistration: reg });
    } catch (e: any) {
      console.warn("[WebPush] getToken failed:", e?.message || e);
      return null;
    }
  }

  private async isWebMessagingSupported(): Promise<boolean> {
    try { return await isSupported(); } catch { return false; }
  }

  private async ensureAndroidChannels(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    if (Capacitor.getPlatform() !== "android") return;

    try {
      await FirebaseMessaging.createChannel({
        id: "orders",
        name: "Orders",
        description: "Order updates",
        importance: Importance.High,
        visibility: Visibility.Public,
        lights: true,
        vibration: true,
        sound: "default",
      });
    } catch {}
  }

  private isIos(): boolean {
    return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";
  }
}
